import { useState, useEffect } from "react";
import "@/App.css";
import { Phone, MessageCircle, MapPin, Clock, Shield, Truck, Star, ChevronRight, Menu, X } from "lucide-react";

// Constants
const WHATSAPP_PRIMARY = "5517996106364";
const WHATSAPP_SECONDARY = "5517991927058";
const PHONE_PRIMARY = "17996106364";
const PHONE_SECONDARY = "17991927058";
const PHONE_DISPLAY_1 = "17 99610-6364";
const PHONE_DISPLAY_2 = "17 99192-7058";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_93bfe065-9570-41a1-9eae-a077e046f05a/artifacts/h4lcaz4m_WhatsApp%20Image%202026-04-02%20at%2016.05.41.jpeg";

const HERO_BG_IMAGE = "https://customer-assets.emergentagent.com/job_guincho-24h-5/artifacts/l4kpljhs_WhatsApp%20Image%202026-04-02%20at%2016.04.08.jpeg";

const GALLERY_IMAGES = [
  "https://customer-assets.emergentagent.com/job_93bfe065-9570-41a1-9eae-a077e046f05a/artifacts/737xapvl_WhatsApp%20Image%202026-04-02%20at%2016.05.09-2.jpeg",
  "https://customer-assets.emergentagent.com/job_93bfe065-9570-41a1-9eae-a077e046f05a/artifacts/k8le8q1v_WhatsApp%20Image%202026-04-02%20at%2016.04.08.jpeg",
  "https://customer-assets.emergentagent.com/job_93bfe065-9570-41a1-9eae-a077e046f05a/artifacts/wv82tvsh_WhatsApp%20Image%202026-04-02%20at%2016.04.03.jpeg",
  "https://customer-assets.emergentagent.com/job_93bfe065-9570-41a1-9eae-a077e046f05a/artifacts/std6b5sc_WhatsApp%20Image%202026-04-02%20at%2016.04.08-3.jpeg",
  "https://customer-assets.emergentagent.com/job_guincho-24h-5/artifacts/c35h2pez_WhatsApp%20Image%202026-04-02%20at%2016.05.09.jpeg",
  "https://customer-assets.emergentagent.com/job_guincho-24h-5/artifacts/g682corl_WhatsApp%20Image%202026-04-02%20at%2016.04.08-2.jpeg",
  "https://customer-assets.emergentagent.com/job_guincho-24h-5/artifacts/6a85ubnw_WhatsApp%20Image%202026-04-02%20at%2016.04.07.jpeg",
  "https://customer-assets.emergentagent.com/job_guincho-24h-5/artifacts/pv2iblbu_WhatsApp%20Image%202026-04-02%20at%2016.04.05-2.jpeg"
];

const REGIONS = [
  "Monte Azul Paulista",
  "Bebedouro",
  "Olímpia",
  "Catanduva",
  "Barretos",
  "Ribeirão Preto",
  "São José do Rio Preto"
];

const SERVICES = [
  { title: "Guincho para veículos leves", icon: "🚗" },
  { title: "Guincho para veículos pesados", icon: "🚛" },
  { title: "Transporte de máquinas", icon: "🏗️" },
  { title: "Transporte náutico", icon: "🚤" },
  { title: "Fretes em geral", icon: "📦" },
  { title: "Remoção de veículos acidentados", icon: "🚨" },
  { title: "Pane mecânica", icon: "🔧" },
  { title: "Pane elétrica", icon: "⚡" },
  { title: "Troca de pneus", icon: "🛞" },
  { title: "Atendimento emergencial", icon: "🆘" },
  { title: "Atendimento 24 horas", icon: "🕐" }
];

const DIFFERENTIALS = [
  { number: "01", title: "Atendimento Rápido", desc: "Chegamos em tempo recorde no local" },
  { number: "02", title: "24 Horas", desc: "Disponível a qualquer hora do dia ou noite" },
  { number: "03", title: "Equipe Especializada", desc: "Profissionais treinados e experientes" },
  { number: "04", title: "Preço Justo", desc: "Valores competitivos sem surpresas" },
  { number: "05", title: "Chegada Rápida", desc: "Cobertura ampla em toda região" },
  { number: "06", title: "Segurança Total", desc: "Transporte seguro do seu veículo" }
];

const REVIEWS = [
  { text: "Atendimento muito rápido, recomendo!", author: "Carlos M." },
  { text: "Chegou em menos de 30 minutos!", author: "Ana Paula S." },
  { text: "Preço justo e serviço profissional", author: "Roberto F." },
  { text: "Guincho cuidadoso com o veículo", author: "Mariana L." },
  { text: "Atendimento excelente 24 horas", author: "João Pedro R." }
];

// Components
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={`header-sticky bg-white ${scrolled ? "scrolled shadow-lg" : ""}`} data-testid="header">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between py-3">
          {/* Mobile: Center logo */}
          <a href="#hero" className="flex items-center gap-3 md:flex-none flex-1 md:justify-start justify-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="HM Auto Socorro" 
              className="h-12 md:h-14 w-auto object-contain"
            />
          </a>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            <a href="#servicos" className="text-gray-700 hover:text-[#fe0000] text-sm font-semibold uppercase tracking-wider transition-colors">Serviços</a>
            <a href="#galeria" className="text-gray-700 hover:text-[#fe0000] text-sm font-semibold uppercase tracking-wider transition-colors">Galeria</a>
            <a href="#avaliacoes" className="text-gray-700 hover:text-[#fe0000] text-sm font-semibold uppercase tracking-wider transition-colors">Avaliações</a>
          </nav>
          
          <div className="hidden md:flex items-center gap-4">
            <a 
              href={`tel:${PHONE_PRIMARY}`}
              className="cta-button cta-button-red text-sm py-3 px-6"
              data-testid="header-call-btn"
            >
              <Phone size={18} />
              Ligar 24h
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gray-800 p-2 absolute right-4"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-btn"
          >
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col gap-4">
              <a href="#servicos" className="text-gray-700 hover:text-[#fe0000] font-semibold" onClick={() => setMenuOpen(false)}>Serviços</a>
              <a href="#galeria" className="text-gray-700 hover:text-[#fe0000] font-semibold" onClick={() => setMenuOpen(false)}>Galeria</a>
              <a href="#avaliacoes" className="text-gray-700 hover:text-[#fe0000] font-semibold" onClick={() => setMenuOpen(false)}>Avaliações</a>
              <a 
                href={`tel:${PHONE_PRIMARY}`}
                className="cta-button cta-button-red text-sm py-3 mt-2"
                data-testid="mobile-call-btn"
              >
                <Phone size={18} />
                Ligar Agora
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const HeroSection = () => {
  return (
    <section id="hero" className="hero-section pt-24 relative" data-testid="hero-section">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG_IMAGE})` }}
      />
      {/* Dark Overlay for text readability */}
      <div className="absolute inset-0 bg-black/70" />
      <div className="hero-grid-bg" />
      <div className="noise-overlay" />
      
      <div className="container mx-auto px-4 md:px-8 py-16 md:py-24 relative z-10">
        <div className="max-w-4xl">
          {/* Overline */}
          <p className="text-[#fe0000] text-sm md:text-base font-bold uppercase tracking-[0.2em] mb-4 animate-fade-in-up">
            <Clock className="inline-block mr-2" size={16} />
            Atendimento 24 Horas
          </p>
          
          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.95] mb-6 animate-fade-in-up" style={{animationDelay: '0.1s'}} data-testid="hero-headline">
            Precisa de<br />
            <span className="text-[#fe0000]">Guincho?</span>
          </h1>
          
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold uppercase tracking-tight text-white/90 mb-8 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            Atendimento Rápido 24 Horas
          </h2>
          
          {/* Description */}
          <p className="text-base md:text-lg text-white/70 max-w-2xl mb-8 leading-relaxed animate-fade-in-up" style={{animationDelay: '0.3s'}}>
            Segurança e eficiência garantida. Seu parceiro de confiança para remoção e transporte de veículos em <strong className="text-white">Monte Azul Paulista</strong> e região.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10 animate-fade-in-up" style={{animationDelay: '0.4s'}}>
            <a 
              href={`https://wa.me/${WHATSAPP_PRIMARY}?text=Olá! Preciso de um guincho.`}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button cta-button-green animate-pulse-urgent"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle size={24} />
              Chamar no WhatsApp
            </a>
            <a 
              href={`tel:${PHONE_PRIMARY}`}
              className="cta-button cta-button-red"
              data-testid="hero-call-btn"
            >
              <Phone size={24} />
              Ligar Agora
            </a>
          </div>
          
          {/* Phone Numbers */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-10 animate-fade-in-up" style={{animationDelay: '0.5s'}}>
            <div className="flex items-center gap-3">
              <Phone className="text-[#32cd33]" size={20} />
              <span className="phone-display text-white">{PHONE_DISPLAY_1}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="text-[#32cd33]" size={20} />
              <span className="phone-display text-white">{PHONE_DISPLAY_2}</span>
            </div>
          </div>
          
          {/* Green highlights */}
          <div className="flex flex-wrap gap-3 mb-8 animate-fade-in-up" style={{animationDelay: '0.55s'}}>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#32cd33]/20 border border-[#32cd33]/50 text-[#32cd33] font-semibold text-sm">
              <Clock size={16} /> 24 HORAS
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#32cd33]/20 border border-[#32cd33]/50 text-[#32cd33] font-semibold text-sm">
              <Shield size={16} /> SEGURANÇA TOTAL
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#32cd33]/20 border border-[#32cd33]/50 text-[#32cd33] font-semibold text-sm">
              <Truck size={16} /> CHEGADA RÁPIDA
            </span>
          </div>
          
          {/* Regions */}
          <div className="animate-fade-in-up" style={{animationDelay: '0.6s'}}>
            <p className="text-base md:text-lg text-white/90 mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-[#32cd33]" />
              <span>Atendimento do <strong className="text-[#32cd33]">interior</strong> à <strong className="text-[#32cd33]">grande São Paulo</strong>.</span>
            </p>
            <p className="text-sm text-white/50 uppercase tracking-wider mb-3">
              Regiões Atendidas
            </p>
            <div className="flex flex-wrap gap-2">
              {REGIONS.map((region) => (
                <span key={region} className="region-chip" data-testid={`region-${region.toLowerCase().replace(/\s/g, '-')}`}>
                  {region}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  return (
    <section id="servicos" className="bg-white text-black py-20 md:py-28" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <p className="text-[#fe0000] text-sm font-bold uppercase tracking-[0.2em] mb-3">
            O que fazemos
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight">
            Nossos Serviços
          </h2>
          <p className="text-gray-600 mt-4 max-w-xl">
            Atendemos todos os tipos de remoção e transporte de veículos com qualidade e segurança.
          </p>
        </div>
        
        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((service, index) => (
            <div 
              key={index}
              className="service-card group"
              data-testid={`service-card-${index}`}
            >
              <div className="flex items-start gap-4">
                <span className="text-3xl">{service.icon}</span>
                <div>
                  <h3 className="font-bold text-lg group-hover:text-[#fe0000] transition-colors">
                    {service.title}
                  </h3>
                  <ChevronRight className="text-[#fe0000] mt-2 opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DifferentialsSection = () => {
  return (
    <section className="bg-[#111] text-white py-20 md:py-28 border-y border-white/10" data-testid="differentials-section">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <p className="text-[#fe0000] text-sm font-bold uppercase tracking-[0.2em] mb-3">
            Por que nos escolher
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight">
            Nossos Diferenciais
          </h2>
        </div>
        
        {/* Differentials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {DIFFERENTIALS.map((diff, index) => (
            <div key={index} className="differential-item" data-testid={`differential-${index}`}>
              <span className="differential-number">{diff.number}</span>
              <h3 className="font-bold text-xl mb-2">{diff.title}</h3>
              <p className="text-white/60">{diff.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const GallerySection = () => {
  return (
    <section id="galeria" className="bg-white py-20 md:py-28" data-testid="gallery-section">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <p className="text-[#fe0000] text-sm font-bold uppercase tracking-[0.2em] mb-3">
            <Truck className="inline-block mr-2" size={16} />
            Trabalhos Realizados
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-black">
            Serviços em Ação
          </h2>
          <p className="text-gray-600 mt-4 max-w-xl">
            Confira alguns atendimentos realizados pela HM Auto Socorro.
          </p>
        </div>
        
        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {GALLERY_IMAGES.map((image, index) => (
            <div 
              key={index}
              className="gallery-image aspect-video"
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={image} 
                alt={`Serviço realizado ${index + 1}`}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ReviewsSection = () => {
  return (
    <section id="avaliacoes" className="reviews-section py-20 md:py-28" data-testid="reviews-section">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-12 md:mb-16 text-center">
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="star-rating fill-current" size={32} />
            ))}
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">
            Avaliações
          </h2>
          <p className="text-white/80 mt-4">
            O que nossos clientes dizem sobre nós
          </p>
        </div>
        
        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {REVIEWS.map((review, index) => (
            <div key={index} className="bg-rgba(0,0,0,0.1) p-6 border-2 border-[#32cd33]" data-testid={`review-${index}`}>
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="star-rating fill-current" size={16} />
                ))}
              </div>
              <p className="font-bold text-lg mb-3">"{review.text}"</p>
              <p className="text-white/70 text-sm">— {review.author}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTASection = () => {
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 text-center relative" data-testid="final-cta-section">
      <div className="hero-grid-bg" />
      <div className="noise-overlay" />
      
      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight mb-4" data-testid="final-cta-headline">
          Precisou?<br />
          <span className="text-[#fe0000]">Ligou!</span>
        </h2>
        
        <p className="text-xl md:text-2xl text-white/70 mb-10">
          Guincho disponível agora mesmo
        </p>
        
        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
          <a 
            href={`https://wa.me/${WHATSAPP_PRIMARY}?text=Olá! Preciso de um guincho urgente.`}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button cta-button-green text-lg md:text-xl py-6 px-10 animate-pulse-urgent"
            data-testid="final-whatsapp-btn"
          >
            <MessageCircle size={28} />
            Chamar no WhatsApp
          </a>
          <a 
            href={`tel:${PHONE_PRIMARY}`}
            className="cta-button cta-button-red text-lg md:text-xl py-6 px-10"
            data-testid="final-call-btn"
          >
            <Phone size={28} />
            Ligar Agora
          </a>
        </div>
        
        {/* Phone Numbers */}
        <div className="flex flex-col sm:flex-row justify-center gap-6 sm:gap-12 mb-12">
          <a href={`tel:${PHONE_PRIMARY}`} className="phone-display text-2xl md:text-3xl text-white hover:text-[#32cd33] transition-colors">
            {PHONE_DISPLAY_1}
          </a>
          <a href={`tel:${PHONE_SECONDARY}`} className="phone-display text-2xl md:text-3xl text-white hover:text-[#32cd33] transition-colors">
            {PHONE_DISPLAY_2}
          </a>
        </div>
        
        {/* Urgency Info */}
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-4 border border-white/10">
              <Clock className="mx-auto mb-2 text-[#fe0000]" size={24} />
              <p className="font-bold text-sm uppercase">Atendimento Imediato</p>
              <p className="text-white/60 text-sm">24 horas</p>
            </div>
            <div className="p-4 border border-white/10">
              <Truck className="mx-auto mb-2 text-[#fe0000]" size={24} />
              <p className="font-bold text-sm uppercase">Equipe Pronta</p>
              <p className="text-white/60 text-sm">Para sair agora</p>
            </div>
            <div className="p-4 border border-white/10">
              <MapPin className="mx-auto mb-2 text-[#fe0000]" size={24} />
              <p className="font-bold text-sm uppercase">Cobertura</p>
              <p className="text-white/60 text-sm">Toda região</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="py-12 md:py-16 bg-[#32cd33]" data-testid="footer">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {/* Logo & Description */}
          <div>
            <img 
              src={LOGO_URL} 
              alt="HM Auto Socorro" 
              className="h-16 w-auto mb-4"
            />
            <p className="text-white/90 text-sm">
              Guincho 24 horas em Monte Azul Paulista - SP e região. Serviço de qualidade com preço justo.
            </p>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-bold uppercase tracking-wider mb-4 text-white">Contato</h4>
            <div className="space-y-3">
              <a 
                href={`https://wa.me/${WHATSAPP_PRIMARY}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white hover:text-black transition-colors"
                data-testid="footer-whatsapp-1"
              >
                <MessageCircle size={18} />
                {PHONE_DISPLAY_1}
              </a>
              <a 
                href={`https://wa.me/${WHATSAPP_SECONDARY}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white hover:text-black transition-colors"
                data-testid="footer-whatsapp-2"
              >
                <MessageCircle size={18} />
                {PHONE_DISPLAY_2}
              </a>
              <p className="flex items-center gap-3 text-white">
                <MapPin size={18} />
                Monte Azul Paulista - SP
              </p>
            </div>
          </div>
          
          {/* Services */}
          <div>
            <h4 className="font-bold uppercase tracking-wider mb-4 text-white">Serviços</h4>
            <ul className="space-y-2 text-white/90 text-sm">
              <li className="flex items-center gap-2"><span className="text-white">✓</span> Guincho leve e pesado</li>
              <li className="flex items-center gap-2"><span className="text-white">✓</span> Remoção de veículos</li>
              <li className="flex items-center gap-2"><span className="text-white">✓</span> Transporte de máquinas</li>
              <li className="flex items-center gap-2"><span className="text-white">✓</span> Atendimento 24 horas</li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-white/30 mt-10 pt-8 text-center text-white/80 text-sm">
          <p>© {new Date().getFullYear()} HM Auto Socorro. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  return (
    <a
      href={`https://wa.me/${WHATSAPP_PRIMARY}?text=Olá! Preciso de um guincho.`}
      target="_blank"
      rel="noopener noreferrer"
      className="floating-whatsapp !bottom-6 !right-6"
      data-testid="floating-whatsapp"
      aria-label="Chamar no WhatsApp"
    >
      <MessageCircle size={32} className="text-white" />
    </a>
  );
};

// Main App
function App() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <DifferentialsSection />
        <GallerySection />
        <ReviewsSection />
        <FinalCTASection />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
